import { FC, Fragment, ReactNode, useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { Campaign, Organization } from 'types';

import CardHeaderTitle from '../Widgets/Cards/CardHeaderTitle';
import { PageSection } from './components/PageSection';
import { GoldStarsCard } from './PersonPerformance/GoldStarsCard';
import { HeadsUpCard } from './PersonPerformance/HeadsUpCard';
import { InfluenceCard } from './PersonPerformance/InfluenceCard';
import NetworkRecognitionInProgress from './PersonPerformance/NetworkRecognitionInProgress';
import PerformanceProfileContext from './PersonPerformance/PersonPerformanceContext';
import { SkillsCard } from './PersonPerformance/SkillsCard';
import GoldStarsDistributionChart from './PersonPerformance/Trajectory/GoldStarsDistributionChart';
import InfluenceDistributionChart from './PersonPerformance/Trajectory/InfluenceDistributionChart';
import NetworkRecognitionHistoryChart from './PersonPerformance/Trajectory/NetworkRecognitionHistoryChart';

interface LayoutColumn {
  size: { xs?: number; md?: number; lg?: number; xl?: number };
  component: ReactNode;
}

// Distributes 12 Bootstrap columns evenly across visible chart entries
const resolveChartSizes = (
  count: number
): Array<{ xs: number; lg: number; xl: number }> => {
  switch (count) {
    case 3:
      return [
        { xs: 12, lg: 6, xl: 4 },
        { xs: 12, lg: 6, xl: 4 },
        { xs: 12, lg: 12, xl: 4 },
      ];
    case 2:
      return [
        { xs: 12, lg: 6, xl: 6 },
        { xs: 12, lg: 6, xl: 6 },
      ];
    case 1:
      return [{ xs: 12, lg: 12, xl: 12 }];
    default:
      return [];
  }
};

interface Props {
  data: any;
  currentPerson: any;
  campaign: Campaign;
  currentOrganization: Organization;
  firstName: string;
  showManagerOnlyPerformanceDetails: boolean;
  performanceData: any;
  isDemoOrPreviewMode?: boolean;
  isInReviewFlow?: boolean | null;
}

export const NetworkRecognitionRow: FC<Props> = ({
  data,
  currentPerson,
  campaign,
  currentOrganization,
  firstName,
  showManagerOnlyPerformanceDetails,
  performanceData,
  isDemoOrPreviewMode = false,
  isInReviewFlow,
}) => {
  const { formatMessage } = useIntl();

  const performanceProfileContext = useContext(PerformanceProfileContext);

  const {
    displayCharts,
    displayOnaDistribution,
    displayManagerDistribution,
    hasNetwork,
    hasGoldStars,
    hasHeadsUps,
  } = performanceProfileContext;

  const campaignId = campaign.id;

  const selectedCampaignData = useMemo(() => {
    if (!data || data.length === 0) return null;
    if (campaignId) return data.find((item) => item.campaign.id === campaignId);
    return null;
  }, [data, campaignId]);

  const positiveONA = useMemo(() => {
    if (!selectedCampaignData) return null;
    return [
      ...(selectedCampaignData.gold_stars ?? []),
      ...(selectedCampaignData.influence ?? []),
    ];
  }, [selectedCampaignData]);

  const layout = useMemo(() => {
    return {
      'influence-distribution': (
        <Card key="influence-distrbution" className="card-fill">
          <CardHeader>
            <CardHeaderTitle>
              <FormattedMessage
                id="app.views.person.networkrecognitionrow.influence_distribution"
                defaultMessage="Influence distribution"
              />
            </CardHeaderTitle>
          </CardHeader>
          <CardBody>
            <InfluenceDistributionChart person={currentPerson} />
          </CardBody>
        </Card>
      ),
      'gold-stars-distribution': (
        <Card key="gold-stars-distrbution" className="card-fill">
          <CardHeader>
            <CardHeaderTitle>
              <FormattedMessage
                id="app.views.person.networkrecognitionrow.gold_stars_distribution"
                defaultMessage="Outstanding impact distribution"
              />
            </CardHeaderTitle>
          </CardHeader>
          <CardBody>
            <GoldStarsDistributionChart person={currentPerson} />
          </CardBody>
        </Card>
      ),
      'network-recognition-history': (
        <NetworkRecognitionHistoryChart
          key="network-recognition-history"
          person={currentPerson}
          showManagerOnlyPerformanceDetails={showManagerOnlyPerformanceDetails}
          performanceData={performanceData}
        />
      ),
      'gold-stars-card': (
        <GoldStarsCard
          organization={currentOrganization}
          person={currentPerson}
          goldStars={selectedCampaignData?.gold_stars}
        />
      ),
      'heads-up-card':
        selectedCampaignData?.heads_ups_display &&
        selectedCampaignData?.heads_ups_display !== 'hidden' ? (
          <HeadsUpCard
            firstName={firstName}
            organization={currentOrganization}
            person={currentPerson}
            headsUp={selectedCampaignData?.heads_ups}
            headsUpsDisplay={selectedCampaignData.heads_ups_display}
          />
        ) : null,
      'influence-card': (
        <InfluenceCard
          organization={currentOrganization}
          person={currentPerson}
          influence={selectedCampaignData?.influence}
        />
      ),
      'skills-card': (
        <SkillsCard
          organization={currentOrganization}
          person={currentPerson}
          // @ts-expect-error
          positiveONA={positiveONA}
          isDemoOrPreviewMode={isDemoOrPreviewMode}
        />
      ),
    };
  }, [
    currentOrganization,
    currentPerson,
    selectedCampaignData,
    positiveONA,
    firstName,
    performanceData,
    showManagerOnlyPerformanceDetails,
    isDemoOrPreviewMode,
  ]);

  // --- Chart columns (ONA distribution mode) ---
  const chartColumns = useMemo((): LayoutColumn[] => {
    if (!displayCharts || !displayOnaDistribution) return [];

    const visibleCharts = [
      hasNetwork && layout['influence-distribution'],
      hasGoldStars && layout['gold-stars-distribution'],
      layout['network-recognition-history'],
    ].filter(Boolean) as ReactNode[];

    const sizes = resolveChartSizes(visibleCharts.length);
    return visibleCharts.map((component, i) => ({
      size: sizes[i],
      component,
    }));
  }, [displayCharts, displayOnaDistribution, hasNetwork, hasGoldStars, layout]);

  // --- Manager distribution columns (history chart + grouped cards) ---
  const managerDistributionColumns = useMemo((): LayoutColumn[] => {
    if (!displayCharts || displayOnaDistribution || !displayManagerDistribution)
      return [];

    const groupedCards = [
      hasGoldStars && layout['gold-stars-card'],
      hasHeadsUps && layout['heads-up-card'],
      hasNetwork && layout['influence-card'],
      (hasGoldStars || hasHeadsUps || hasNetwork) && layout['skills-card'],
    ].filter(Boolean) as ReactNode[];

    const columns: LayoutColumn[] = [
      {
        size: { xs: 12, lg: 12, xl: groupedCards.length > 0 ? 6 : 12 },
        component: layout['network-recognition-history'],
      },
    ];

    if (groupedCards.length > 0) {
      columns.push({
        size: { xs: 12, lg: 12, xl: 6 },
        component: <Fragment key="ona-group">{groupedCards}</Fragment>,
      });
    }

    return columns;
  }, [
    displayCharts,
    displayOnaDistribution,
    displayManagerDistribution,
    hasNetwork,
    hasGoldStars,
    hasHeadsUps,
    layout,
  ]);

  // --- Card columns (shown when no charts, charts without distributions, or with ONA distribution) ---
  const cardColumns = useMemo((): LayoutColumn[] => {
    const showCardSection =
      !displayCharts ||
      (!displayOnaDistribution && !displayManagerDistribution) ||
      displayOnaDistribution;

    if (!showCardSection) return [];

    const calloutsComponents = [
      hasGoldStars && layout['gold-stars-card'],
      hasHeadsUps && layout['heads-up-card'],
    ].filter(Boolean) as ReactNode[];

    const networkComponents = [
      hasNetwork && layout['influence-card'],
      (hasGoldStars || hasHeadsUps || hasNetwork) && layout['skills-card'],
    ].filter(Boolean) as ReactNode[];

    const hasBothGroups =
      calloutsComponents.length > 0 && networkComponents.length > 0;

    if (hasBothGroups) {
      return [
        {
          size: { xs: 12, xl: 8 },
          component: <Fragment key="callouts">{calloutsComponents}</Fragment>,
        },
        {
          size: { xs: 12, xl: 4 },
          component: <Fragment key="network">{networkComponents}</Fragment>,
        },
      ];
    }

    // Single group gets full width
    const allComponents = [...calloutsComponents, ...networkComponents];
    if (allComponents.length > 0) {
      return [
        {
          size: { xs: 12 },
          component: <Fragment key="cards">{allComponents}</Fragment>,
        },
      ];
    }

    return [];
  }, [
    displayCharts,
    displayOnaDistribution,
    displayManagerDistribution,
    hasNetwork,
    hasGoldStars,
    hasHeadsUps,
    layout,
  ]);

  const componentsLayout = useMemo(
    () => [...chartColumns, ...managerDistributionColumns, ...cardColumns],
    [chartColumns, managerDistributionColumns, cardColumns]
  );

  return (
    <PageSection
      id="ona-summary-table"
      title={
        <>
          {formatMessage({
            id: 'app.views.person.network_recognition_row.title.network_recognition',
            defaultMessage: 'Network recognition',
          })}
          {performanceProfileContext.isSelfPhaseInProgress &&
            !!isInReviewFlow && <NetworkRecognitionInProgress />}
        </>
      }
      titleIcon="share-2"
      titleIconColor="text-primary"
      widths={componentsLayout.map((x) => x.size)}
    >
      {componentsLayout.map((x) => x.component)}
    </PageSection>
  );
};
